import { Suspense, useEffect } from "react";
import { loadTechCategoryRegistry } from "./pages/crm/partners/techCategoryRegistry";
// ★ 문서 라우트 경로의 정본은 documentRegistry.ts — 경로를 고치려면 거기서 고친다.
import { automationRouteSegment, crmRouteSegment } from "./pages/automation/common/documentRegistry";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import lazyWithRetry from "./utils/lazyWithRetry";
import { getLastProduct } from "./utils/lastProduct";
import PageLayout from "./components/layout/PageLayout";
// /home = 시네마틱 뉴럴 히어로. 직전 운영 홈(마인드맵 Flow 판)은 /home_demo 로 물러났다.
const NeuralHeroDemoPage = lazyWithRetry(() => import("./pages/home/NeuralHeroDemoPage"));
const HomePage = lazyWithRetry(() => import("./pages/HomePage"));
// Care Desk 접수·스케줄 POC (IT-CONSULTING › POC) — 전부 목 데이터, API 미사용
const CareDeskPocPage = lazyWithRetry(() => import("./pages/crm/careDesk/CareDeskPocPage"));
const RfqPocPage = lazyWithRetry(() => import("./pages/automation/poc/rfq/RfqPocPage"));
// HWP 서식 편집·출력 POC (IT-CONSULTING › POC) — 파싱까지 전부 브라우저에서, 업로드 없음
const HwpFormStudioPocPage = lazyWithRetry(() => import("./pages/automation/poc/HwpFormStudioPocPage"));
const EquipmentCatalogPage = lazyWithRetry(() => import("./pages/catalog/EquipmentCatalogPage"));
const DetailPage = lazyWithRetry(() => import("./pages/catalog/DetailPage"));
const SystemCatalogPage = lazyWithRetry(() => import("./pages/catalog/SystemCatalogPage"));
const DiagnosisLogicPage = lazyWithRetry(() => import("./pages/diagnosis/DiagnosisLogicPage"));
const DiagnosisResultPdfPage = lazyWithRetry(() => import("./pages/diagnosis/DiagnosisResultPdfPage"));
const NextStepsPage = lazyWithRetry(() => import("./pages/Nextstepspage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/Privacypolicypage"));
const MarketingPolicyPage = lazyWithRetry(() => import("./pages/Marketingpolicypage"));
const ContactPage = lazyWithRetry(() => import("./pages/ContactPage"));
const PartnerInfraPage = lazyWithRetry(() => import("./pages/PartnerInfraPage"));
const InvoiceGuidePage = lazyWithRetry(() => import("./pages/automation/invoice-guide/InvoiceGuidePage"));
const MOUStatusPage = lazyWithRetry(() => import("./pages/automation/MOUStatusPage"));
const AutomationDbPage = lazyWithRetry(() => import("./pages/automation/AutomationDbPage"));
const SimulatorPage = lazyWithRetry(() => import("./pages/simulator/SimulatorPage"));
const Brand501Page = lazyWithRetry(() => import("./pages/settlement/Brand501Page"));
// SalesPipelinePlanningPage import removed — 내부 기획안(Draft v0.2) 페이지로 /crm/pipeline redirect 처리
const RoiAnalysisPage = lazyWithRetry(() => import("./features/roi-analysis/pages/RoiAnalysisPage"));
// PoC「ARENA by Colosseum」 — MRO 편집샵 + USED 장비 마켓. features/mro 에 격리(docs/POC_MRO_ARENA/PLAN.md).
const ArenaLayout = lazyWithRetry(() => import("./features/mro/pages/ArenaLayout"));
const ArenaHomePage = lazyWithRetry(() => import("./features/mro/pages/ArenaHomePage"));
const ArenaSupplyListPage = lazyWithRetry(() => import("./features/mro/pages/SupplyListPage"));
const ArenaKitListPage = lazyWithRetry(() => import("./features/mro/pages/KitListPage"));
const ArenaDealPage = lazyWithRetry(() => import("./features/mro/pages/DealPage"));
const ArenaBrandListPage = lazyWithRetry(() => import("./features/mro/pages/BrandListPage"));
const ArenaBrandDetailPage = lazyWithRetry(() => import("./features/mro/pages/BrandDetailPage"));
const ArenaReferencePage = lazyWithRetry(() => import("./features/mro/pages/ReferencePage"));
const ArenaUsedHomePage = lazyWithRetry(() => import("./features/mro/pages/UsedHomePage"));
const ArenaUsedListPage = lazyWithRetry(() => import("./features/mro/pages/UsedListPage"));
const ArenaProductDetailPage = lazyWithRetry(() => import("./features/mro/pages/ProductDetailPage"));
const ArenaWishlistPage = lazyWithRetry(() => import("./features/mro/pages/WishlistPage"));
const ArenaCartPage = lazyWithRetry(() => import("./features/mro/pages/CartPage"));
const ArenaCheckoutPage = lazyWithRetry(() => import("./features/mro/pages/CheckoutPage"));
const ArenaOrderCompletePage = lazyWithRetry(() => import("./features/mro/pages/OrderCompletePage"));
const ArenaOrdersPage = lazyWithRetry(() => import("./features/mro/pages/OrdersPage"));
const ArenaInquiryPage = lazyWithRetry(() => import("./features/mro/pages/InquiryPage"));
// 기획 문서 관리 — 파일 탐색기 + 문서 미리보기(react-markdown 포함)라 lazy 로드
const PlanningDocumentsPage = lazyWithRetry(() => import("./pages/planning/PlanningDocumentsPage"));
const OAuth2CallbackPage = lazyWithRetry(() => import("./pages/OAuth2CallbackPage"));
const MwmsApiTestPage = lazyWithRetry(() => import("./pages/api-test/MwmsApiTestPage"));
const ReservationPage = lazyWithRetry(() => import("./pages/reservation/ReservationPage"));
const MOUStatusStandalonePage = lazyWithRetry(() => import("./pages/automation/MOUStatusStandalonePage"));
const CargoFormPage = lazyWithRetry(() => import("./pages/cargo/CargoFormPage"));

// CRM
// 틀은 메뉴가 놓인 대메뉴를 따른다(메뉴 관리에서 옮긴 메뉴) — AreaFrame
import AreaFrame from "./pages/AreaFrame";
// MARKETING (이벤트 참석자 모듈) — CRM 하위 메뉴/라우트
const EventHubPage = lazyWithRetry(() => import("./pages/marketing/EventHubPage"));
const MarketingDashboardPage = lazyWithRetry(() => import("./pages/marketing/MarketingDashboardPage"));
// IT-CONSULTING › 현장 리서치 — 주소는 /crm/field-research(사용자 지정), 틀은 IT-CONSULTING(AreaFrame home="automation")
const FieldResearchEventsPage = lazyWithRetry(() => import("./pages/crm/field-research/FieldResearchEventsPage"));
const FieldResearchEventPage = lazyWithRetry(() => import("./pages/crm/field-research/FieldResearchEventPage"));
const FieldResearchCompanyPage = lazyWithRetry(() => import("./pages/crm/field-research/FieldResearchCompanyPage"));
const FieldResearchQuestionsPage = lazyWithRetry(() => import("./pages/crm/field-research/FieldResearchQuestionsPage"));
// 디지털 트윈 3D 샌드박스 (three 의존 — lazy 로 메인 번들 분리)
const DtwinSandboxPage = lazyWithRetry(() => import("./features/digitalTwin/pages/DtwinSandboxPage"));
const DtwinStackerSamplePage = lazyWithRetry(() => import("./features/digitalTwin/pages/DtwinStackerSamplePage"));
const DtwinWorkerMotionPage = lazyWithRetry(() => import("./features/digitalTwin/pages/DtwinWorkerMotionPage"));
const DtwinWorkerMotionV2Page = lazyWithRetry(() => import("./features/digitalTwin/pages/DtwinWorkerMotionV2Page"));
const CRMDashboardPage = lazyWithRetry(() => import("./pages/crm/CRMDashboardPage"));
const CRMCalendarPage = lazyWithRetry(() => import("./pages/crm/calendar/CRMCalendarPage"));
const CRMPipelinePage = lazyWithRetry(() => import("./pages/crm/CRMPipelinePage"));
const CRMProjectsPage = lazyWithRetry(() => import("./pages/crm/CRMProjectsPage"));
// Overview › Opportunity 현황 — 임원 보고·확인용 읽기 전용 표(비고만 기록 가능)
const OpportunityOverviewPage = lazyWithRetry(() => import("./pages/crm/OpportunityOverviewPage"));
const TaskManagementPage = lazyWithRetry(() => import("./pages/crm/tasks/TaskManagementPage"));
const FlowBuilderRedirect = lazyWithRetry(() => import("./pages/crm/FlowBuilderRedirect"));
// 문서 통합 작성 화면 셸 (/crm/documents/:docKey) — 기존 문서 경로는 그대로 살아 있다.
const DocumentWorkspacePage = lazyWithRetry(() => import("./pages/crm/documents/DocumentWorkspacePage"));
// 구 문서 경로 → 정본 경로 리다이렉트(쿼리·해시 보존). 라우트에서 바로 쓰므로 lazy 가 아니다.
import DocumentRouteRedirect from "./pages/crm/documents/DocumentRouteRedirect";
const LeadInboxPage = lazyWithRetry(() => import("./pages/crm/LeadInboxPage"));
const MailboxPage = lazyWithRetry(() => import("./pages/crm/MailboxPage"));
const CompanyContactsPage = lazyWithRetry(() => import("./pages/crm/CompanyContactsPage"));
const ContractsPage = lazyWithRetry(() => import("./pages/crm/contracts/ContractsPage"));
const ContractDetailPage = lazyWithRetry(() => import("./pages/crm/contracts/ContractDetailPage"));
const ApiConsolePage = lazyWithRetry(() => import("./pages/crm/ApiConsolePage"));
const EmailTemplateBuilderPage = lazyWithRetry(() => import("./pages/crm/EmailTemplateBuilderPage"));
const AutomationRoadmapPage = lazyWithRetry(() => import("./pages/crm/AutomationRoadmapPage"));
const LeadMailRulesPage = lazyWithRetry(() => import("./pages/crm/LeadMailRulesPage"));
const SlackTemplatePage = lazyWithRetry(() => import("./pages/crm/SlackTemplatePage"));
const CommunicationTemplatesPage = lazyWithRetry(() => import("./pages/crm/CommunicationTemplatesPage"));
const LeadTestPage = lazyWithRetry(() => import("./pages/crm/LeadTestPage"));
const EmailSendTestPage = lazyWithRetry(() => import("./pages/crm/EmailSendTestPage"));
const ProjectFlowPage = lazyWithRetry(() => import("./pages/crm/ProjectFlowPage"));
// WBS 전체 — 딜별 Lead·Opportunity·수행 구간 간트 + 최근 변경(편집은 딜 상세 WBS 탭)
const WbsPortfolioPage = lazyWithRetry(() => import("./pages/crm/wbs/WbsPortfolioPage"));
const MeetingNotesPocPage = lazyWithRetry(() => import("./pages/crm/MeetingNotesPocPage"));
const PipelineSettingsPage = lazyWithRetry(() => import("./pages/crm/PipelineSettingsPage"));
const PartnerCapabilitiesPage = lazyWithRetry(() => import("./pages/crm/PartnerCapabilitiesPage"));
const PartnerVerificationPage = lazyWithRetry(() => import("./pages/crm/partner-verification/PartnerVerificationPage"));
const PartnerDetailPage = lazyWithRetry(() => import("./pages/crm/partners/PartnerDetailPage"));
const PartnerAnalysisPage = lazyWithRetry(() => import("./pages/crm/partners/PartnerAnalysisPage"));
const CompanyAssetsPage = lazyWithRetry(() => import("./pages/crm/CompanyAssetsPage"));
const CRMFilesPage = lazyWithRetry(() => import("./pages/crm/CRMFilesPage"));
const LogisticsDataLabPage = lazyWithRetry(() => import("./pages/crm/LogisticsDataLabPage"));
const MyPage = lazyWithRetry(() => import("./pages/crm/MyPage"));
const OrganizationSettingsPage = lazyWithRetry(() => import("./pages/crm/OrganizationSettingsPage"));
const ProposalPage = lazyWithRetry(() => import("./pages/crm/ProposalPage"));
const ProposalHubPage = lazyWithRetry(() => import("./pages/automation/proposalBuilder/ProposalHubPage"));
const CRMHtmlSamplePage = lazyWithRetry(() => import("./pages/crm/CRMHtmlSamplePage"));
const WmsBillingSimulationPage = lazyWithRetry(() => import("./pages/crm/WmsBillingSimulationPage"));
const PartnersPage = lazyWithRetry(() => import("./pages/crm/partners/PartnersPage"));
const WarehouseRackPocPage = lazyWithRetry(() => import("./pages/crm/WarehouseRackPocPage"));
// Twin Studio (TEST) — 창고 3D Rack + 3D 시뮬레이션을 한 화면에 담는 테스트 메뉴(기존 두 페이지는 그대로)
const TwinStudioPocPage = lazyWithRetry(() => import("./features/twin-studio/pages/TwinStudioPocPage"));
const VaultContentPage = lazyWithRetry(() => import("./pages/automation/VaultContentPage"));
const CatalogEquipmentPage = lazyWithRetry(() => import("./pages/automation/equipmentCatalog/EquipmentCatalogPage"));
const SharedWarehouseRackViewerPage = lazyWithRetry(() => import("./pages/share/SharedWarehouseRackViewerPage"));
const SharedDocViewerPage = lazyWithRetry(() => import("./pages/share/SharedDocViewerPage"));
const SharedMinutesViewerPage = lazyWithRetry(() => import("./pages/share/SharedMinutesViewerPage"));
const SharedWbsViewerPage = lazyWithRetry(() => import("./pages/share/SharedWbsViewerPage"));
// 수신자 지정 레이아웃 뷰어 — 파일을 주지 않고 읽기 전용 2D/3D 화면만 연다
const SharedLayoutViewerPage = lazyWithRetry(() => import("./pages/share/SharedLayoutViewerPage"));
const SharedEquipmentViewerPage = lazyWithRetry(() => import("./pages/share/SharedEquipmentViewerPage")); // [SH1] 수신자 지정 설비 구성
import { ToastProvider } from "./components/ui/Toast";
import { ConfirmProvider } from "./components/ui/ConfirmDialog";
import { ThemeProvider } from "./components/ui/ThemeProvider";
import { LanguageProvider, useLanguage } from "./components/ui/LanguageProvider";
import CrmRuntimeTranslator from "./pages/crm/CrmRuntimeTranslator";
import LoginPage from "./pages/auth/LoginPage";
import AuthCallbackPage from "./pages/auth/AuthCallbackPage";
import AuthGuard from "./components/auth/AuthGuard";
import GuestGuard from "./components/auth/GuestGuard";
import RoleGuard from "./components/auth/RoleGuard";
import { AuthProvider } from "./components/auth/AuthContext";
import { MenuRoleProvider, useMenuRoles } from "./components/auth/MenuRoleContext";
import { DocumentCatalogProvider } from "./components/auth/DocumentCatalogContext";
import { IntegrationsProvider } from "./components/integrations/IntegrationsContext";
const SettingsLayout = lazyWithRetry(() => import("./pages/settings/SettingsLayout"));
const UserManagementPage = lazyWithRetry(() => import("./pages/admin/UserManagementPage"));
const MenuRoleManagementPage = lazyWithRetry(() => import("./pages/admin/MenuRoleManagementPage"));
const DocumentCatalogPage = lazyWithRetry(() => import("./pages/admin/DocumentCatalogPage"));
const AnnouncementManagementPage = lazyWithRetry(() => import("./pages/admin/AnnouncementManagementPage"));
const MeetingNoteViewPage = lazyWithRetry(() => import("./pages/crm/MeetingNoteViewPage"));
const IntegrationSettingsPage = lazyWithRetry(() => import("./pages/settings/IntegrationSettingsPage"));
const DataFlowPage = lazyWithRetry(() => import("./pages/settings/DataFlowPage"));
const MasterDataPage = lazyWithRetry(() => import("./pages/settings/MasterDataPage"));
const AccountSettingsPage = lazyWithRetry(() => import("./pages/settings/AccountSettingsPage"));



// 루트 착지 — CRM 대시보드. 권한이 없으면 들어갈 수 있는 제품(없으면 /home)으로 보낸다.
// 메뉴 권한 매핑이 로드되기 전 코드 기본값으로 고르면 등급을 조정한 조직에서 잘못 보낸다 → 로드까지 대기.
function RootRedirect() {
  const { visible, loaded } = useMenuRoles();
  if (!loaded) return null;
  return <Navigate to={getLastProduct(visible)} replace />;
}

function PageLayoutWithCrmTranslation() {
  const location = useLocation();
  // 기술 분류 DB 목록(설비 카탈로그 「기술 분류」 탭) → 화면의 코드 목록에 반영 — 로그인 뒤 한 번(실패하면 코드 파일 목록 그대로)
  useEffect(() => { void loadTechCategoryRegistry(); }, []);
  const { language } = useLanguage();
  const layout = <PageLayout />;

  if (!location.pathname.startsWith("/crm")) return layout;

  return <CrmRuntimeTranslator language={language}>{layout}</CrmRuntimeTranslator>;
}

export default function App() {
  const protectedLayout = <AuthGuard><PageLayoutWithCrmTranslation /></AuthGuard>;
  const viewerLayout = <RoleGuard minRole="viewer" />;
  // 상단 메뉴 권한으로 영역 진입 가드 — 게스트 등 하위 등급도 상단 메뉴가 허용하면 진입 가능(하위 페이지는 레이아웃이 재검사).
  const crmArea = <RoleGuard menuKey="topnav:CRM" minRole="viewer" />;
  const itcArea = <RoleGuard menuKey="topnav:IT-CONSULTING" minRole="viewer" />;
  // 설정 통합 — 관리자 페이지는 /settings 하위(SettingsLayout). 메뉴별 등급은 메뉴 권한 관리 > 설정 메뉴 탭에서
  // super_admin 이 정한다(settings:* 키, 게스트~슈퍼관리자 — src/data/settingsMenus.ts). 기본값은 settingsMenus 가 쥔다.
  const adminUsersPage = <RoleGuard minRole="super_admin" menuKey="settings:users"><UserManagementPage /></RoleGuard>;
  const adminMenusPage = <RoleGuard minRole="admin" menuKey="settings:menus"><MenuRoleManagementPage /></RoleGuard>;
  const adminDocumentsPage = <RoleGuard minRole="admin" menuKey="settings:documents"><DocumentCatalogPage /></RoleGuard>;
  const adminOrganizationPage = <RoleGuard minRole="super_admin" menuKey="settings:organization"><OrganizationSettingsPage /></RoleGuard>;
  // 공지 = 전원이 보는 배너다. 메뉴 권한과 같은 급으로 admin+ 가 다룬다.
  const adminAnnouncementsPage = <RoleGuard minRole="admin" menuKey="settings:announcements"><AnnouncementManagementPage /></RoleGuard>;
  // 산출물 연결 1차 — 포트 전체 보기 · 시험 실행(읽기 전용)
  const adminDataFlowPage = <RoleGuard minRole="admin" menuKey="settings:data-flow"><DataFlowPage /></RoleGuard>;
  // 마스터 데이터(Application · Opportunity 상태) — 쓰기는 admin+(서버도 같은 기준)
  const adminMasterDataPage = <RoleGuard minRole="admin" menuKey="settings:master-data"><MasterDataPage /></RoleGuard>;

  return (
    <ThemeProvider>
    <LanguageProvider>
    <ToastProvider>
    <ConfirmProvider>
    <AuthProvider>
    <MenuRoleProvider>
    <DocumentCatalogProvider>
    <IntegrationsProvider>
    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
    <Routes>
      <Route path="/login" element={<GuestGuard><LoginPage /></GuestGuard>} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/oauth2callback" element={<OAuth2CallbackPage />} />
      <Route path="/reservation" element={<ReservationPage />} />
      <Route path="/cargo-form" element={<CargoFormPage />} />
      <Route path="/mou-status" element={<MOUStatusStandalonePage />} />
      <Route path="/share/warehouse-rack/:shareId" element={<SharedWarehouseRackViewerPage />} />
      <Route path="/share/doc/:token" element={<SharedDocViewerPage />} />
      {/* 회의록 공유 — 본문만 보여 준다(전사본·제안서 정보는 서버가 안 내려준다). */}
      <Route path="/share/minutes/:token" element={<SharedMinutesViewerPage />} />
      <Route path="/share/wbs/:token" element={<SharedWbsViewerPage />} />
      <Route path="/share/layout/:token" element={<SharedLayoutViewerPage />} />
      <Route path="/share/equipment/:token" element={<SharedEquipmentViewerPage />} />
      {/* 로그인 사용자용 설비 구성 주소 — 메일 없이 복사해서 건넨다. 받은 주소 확인 대신 앱 로그인(AuthGuard) */}
      <Route path="/view/equipment/:token" element={<AuthGuard><SharedEquipmentViewerPage internal /></AuthGuard>} />
      <Route element={protectedLayout}>
        <Route path="/" element={<RootRedirect />} />
        <Route
          path="/home"
          element={
            <Suspense fallback={<div style={{ minHeight: "100vh", background: "#02040a" }} />}>
              <NeuralHeroDemoPage />
            </Suspense>
          }
        />
        {/* 직전 홈 — 되돌릴 때를 대비해 남겨 둔다. */}
        <Route
          path="/home_demo"
          element={
            <Suspense fallback={<div style={{ minHeight: "100vh", background: "#07090f" }} />}>
              <HomePage />
            </Suspense>
          }
        />

        {/* 설정 — 좌측 사이드바(SettingsLayout): 앱 연동(모든 유저) + 관리자(유저·메뉴 권한, admin+) 통합.
            계정 메뉴의 개별 항목을 한 페이지의 좌측 메뉴로 병합. */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="/settings/profile" replace />} />
          <Route element={<AccountSettingsPage />}>
            <Route path="profile" element={<MyPage />} />
            <Route path="integrations" element={<IntegrationSettingsPage />} />
          </Route>
          <Route path="users" element={adminUsersPage} />
          <Route path="menus" element={adminMenusPage} />
          <Route path="documents" element={adminDocumentsPage} />
          <Route path="organization" element={adminOrganizationPage} />
          <Route path="announcements" element={adminAnnouncementsPage} />
          <Route path="data-flow" element={adminDataFlowPage} />
          <Route path="master-data" element={adminMasterDataPage} />
        </Route>

        {/* /home 제외 전체 = viewer 이상 (guest는 default-deny — 게스트 허용 라우트는 이 그룹 밖에 명시) */}
        <Route element={viewerLayout}>
        <Route path="/homepage/catalog/equipment" element={<EquipmentCatalogPage />} />
        <Route path="/homepage/catalog/equipment/:slug" element={<DetailPage />} />
        <Route path="/homepage/catalog/system" element={<SystemCatalogPage />} />
        <Route path="/homepage/catalog/system/:slug" element={<DetailPage />} />
        <Route path="/homepage/nextstep" element={<NextStepsPage />} />
        <Route path="/homepage/privacypolicy" element={<PrivacyPolicyPage />} />
        <Route path="/homepage/marketingpolicy" element={<MarketingPolicyPage />} />

        <Route path="/diagnosis/logic" element={<DiagnosisLogicPage />} />
        <Route path="/diagnosis/pdf" element={<DiagnosisResultPdfPage />} />

        <Route path="/contact" element={<ContactPage />} />
        <Route path="/partner-infra" element={<PartnerInfraPage />} />
        </Route>
        {/* IT-CONSULTING(문서 자동화) 영역 — 상단 메뉴 topnav:IT-CONSULTING 권한으로 진입 */}
        <Route element={itcArea}>
        {/* Automation – nested layout with left side menu */}
        <Route path="/automation" element={<AreaFrame home="automation" />}>
          <Route path="vault/content" element={<VaultContentPage />} />
          <Route path="equipment-catalog" element={<CatalogEquipmentPage />} />
          {/* 분류 체계 · 적재물 규격 화면은 없앴다(2026-10-07) — 분류 편집은 카탈로그 왼쪽 트리 ⋯ */}
          <Route path="equipment-catalog/classes" element={<Navigate to="/automation/equipment-catalog" replace />} />
          <Route path="equipment-catalog/load-units" element={<Navigate to="/automation/equipment-catalog" replace />} />
          {/* 문서 작성은 CRM 정본 경로로 옮겼다 — 한 번 더 튕기지 않게 바로 보낸다. */}
          <Route index element={<Navigate to="/crm/documents/quote" replace />} />
          <Route path={automationRouteSegment("quote")} element={<DocumentRouteRedirect docKey="quote" />} />
          <Route path={automationRouteSegment("quote-other")} element={<DocumentRouteRedirect docKey="quote-other" />} />
          <Route path={automationRouteSegment("contract")} element={<DocumentRouteRedirect docKey="contract" />} />
          <Route path={automationRouteSegment("invoice")} element={<DocumentRouteRedirect docKey="invoice" />} />
          <Route path={automationRouteSegment("mou")} element={<DocumentRouteRedirect docKey="mou" />} />
          <Route path={automationRouteSegment("maintenance-mou")} element={<DocumentRouteRedirect docKey="maintenance-mou" />} />
          <Route path={automationRouteSegment("mou-dematic")} element={<DocumentRouteRedirect docKey="mou-dematic" />} />
          <Route path={automationRouteSegment("nda")} element={<DocumentRouteRedirect docKey="nda" />} />
          <Route path={automationRouteSegment("order-approval")} element={<DocumentRouteRedirect docKey="order-approval" />} />
          <Route path="invoice-guide" element={<InvoiceGuidePage />} />
          <Route path="simulator" element={<SimulatorPage />} />
          <Route path="diagnosis/logic" element={<DiagnosisLogicPage />} />
          <Route path="mou-status" element={<Navigate to="/automation/db/mou-status" replace />} />
          <Route path="db/document-data" element={<AutomationDbPage />} />
          <Route path="db/mou-status" element={<MOUStatusPage />} />
          <Route path="poc/warehouse-rack" element={<WarehouseRackPocPage />} />
          <Route path="poc/twin-studio" element={<TwinStudioPocPage />} />
          <Route path="poc/mwms-api-test" element={<MwmsApiTestPage />} />
          <Route path="poc/wms-billing-simulation" element={<WmsBillingSimulationPage />} />
          <Route path="poc/meeting-notes" element={<MeetingNotesPocPage />} />
          <Route path="poc/care-desk" element={<CareDeskPocPage />} />
          <Route path="poc/rfq" element={<RfqPocPage />} />
          <Route path="poc/hwp-form" element={<HwpFormStudioPocPage />} />
          <Route path="poc/tasks" element={<TaskManagementPage />} />
          <Route path="poc/proposal" element={<ProposalHubPage />} />
          <Route path="poc/proposal-compose" element={<ProposalHubPage initialTab="compose" />} />
          <Route path="poc/proposal-import" element={<ProposalHubPage initialTab="import" />} />
          <Route path="poc/proposal-templates" element={<ProposalHubPage initialTab="templates" />} />
          <Route path="poc/proposal-assets" element={<ProposalHubPage initialTab="assets" />} />
          {/* 포맷 · 디자인 탭은 뺐다(2026-10-01) — 잠깐 있던 주소는 제안서 빌더로 */}
          <Route path="poc/proposal-design" element={<Navigate to="/automation/poc/proposal" replace />} />
          {/* PPTX 뜯어보기(샌드박스) 탭은 뺐다(2026-10-01) — 옛 주소는 제안서 빌더로 */}
          <Route path="poc/proposal-builder" element={<Navigate to="/automation/poc/proposal" replace />} />
        </Route>
        {/* ── 현장 리서치 (IT-CONSULTING) ─────────────────────────────────────
            ★ 주소는 /crm/… 이지만 메뉴는 IT-CONSULTING 섹션이다 — /crm/poc/mro 와 같은 형제 선언.
              react-router 는 정적 세그먼트가 많은 이 경로를 /crm/* 보다 높게 랭크한다.
              itcArea(topnav:IT-CONSULTING) 안이라 진입 권한도 IT-CONSULTING 을 따른다. */}
        <Route path="/crm/field-research" element={<AreaFrame home="automation" />}>
          <Route index element={<FieldResearchEventsPage />} />
          <Route path="questions" element={<FieldResearchQuestionsPage />} />
          <Route path=":eventId" element={<FieldResearchEventPage />} />
          <Route path=":eventId/companies/:companyId" element={<FieldResearchCompanyPage />} />
        </Route>
        {/* 구 경로 → POC 메뉴로 이동 (북마크 보존) */}
        <Route path="/tools/mwms-api-test" element={<Navigate to="/automation/poc/mwms-api-test" replace />} />

        {/* ── PoC ARENA (MRO 편집샵) ─────────────────────────────────────────
            ★ CRMLayout 밖 형제 라우트다. 안에 두면 /crm/* catch-all 에 먹히고
              240px CRM 사이드바가 따라온다. react-router 는 정적 세그먼트가 많은
              이 경로를 /crm/* 보다 높게 랭크하므로 형제 선언으로 충분하다.
            ★ 샵 모드: PageLayout 의 `/crm` 분기가 전역 상단바를 이미 숨긴다. */}
        <Route path="/crm/poc/mro" element={<ArenaLayout />}>
          <Route index element={<ArenaHomePage />} />
          <Route path="supply" element={<ArenaSupplyListPage />} />
          <Route path="kit" element={<ArenaKitListPage />} />
          <Route path="deal" element={<ArenaDealPage />} />
          <Route path="brand" element={<ArenaBrandListPage />} />
          <Route path="brand/:slug" element={<ArenaBrandDetailPage />} />
          <Route path="reference" element={<ArenaReferencePage />} />
          <Route path="used" element={<ArenaUsedHomePage />} />
          <Route path="used/list" element={<ArenaUsedListPage />} />
          <Route path="product/:code" element={<ArenaProductDetailPage />} />
          <Route path="wishlist" element={<ArenaWishlistPage />} />
          <Route path="cart" element={<ArenaCartPage />} />
          <Route path="checkout" element={<ArenaCheckoutPage />} />
          <Route path="orders" element={<ArenaOrdersPage />} />
          <Route path="order/:orderNo" element={<ArenaOrderCompletePage />} />
          <Route path="inquiry" element={<ArenaInquiryPage />} />
          <Route path="*" element={<Navigate to="/crm/poc/mro" replace />} />
        </Route>

        <Route path="/planning" element={<AreaFrame home="automation" />}>
          {/* sales-pipeline: 내부 기획안(Draft v0.2) — 실사용 CRM Pipeline으로 redirect */}
          <Route path="sales-pipeline" element={<Navigate to="/crm/pipeline" replace />} />
          <Route path="logistics-roi-practice" element={<Navigate to="/planning/roi-analysis" replace />} />
          <Route path="roi-analysis" element={<RoiAnalysisPage />} />
          <Route path="documents" element={<PlanningDocumentsPage />} />
        </Route>

        <Route path="/settlement" element={<AreaFrame home="automation" />}>
          <Route path="brand501" element={<Brand501Page />} />
        </Route>
        </Route>

        {/* CRM 영역 — 상단 메뉴 topnav:CRM 권한으로 진입 (게스트도 허용 시 진입, 하위는 CRMLayout이 재검사) */}
        <Route element={crmArea}>
        {/* CRM – nested layout with left side menu */}
        <Route path="/crm" element={<AreaFrame home="crm" />}>
          <Route index element={<Navigate to="/crm/dashboard" replace />} />
          <Route path="my-page" element={<Navigate to="/settings/profile" replace />} />
          {/* 조직 관리는 /settings(관리자)로 이관 — 구 경로 북마크 보존 */}
          <Route path="settings/organization" element={<Navigate to="/settings/organization" replace />} />
          <Route path="dashboard" element={<CRMDashboardPage />} />
          <Route path="calendar" element={<CRMCalendarPage />} />
          <Route path="opportunity-overview" element={<OpportunityOverviewPage />} />
          <Route path="leads" element={<LeadInboxPage />} />
          <Route path="mailbox" element={<MailboxPage />} />
          <Route path="partners" element={<PartnersPage />} />
          {/* 회사·담당자 통합 — 옛 /crm/accounts · /crm/contacts · /crm/company-contacts 는 없앴다(구독 상세 포함 전부 여기). */}
          <Route path="relationships" element={<CompanyContactsPage />} />
          {/* 계약서 — 유형(표준하도급·WMS·SI·MOU·NDA)을 한 화면에서 고른다.
              메뉴 등록은 커밋 9(기존 Contract 그룹 정리와 함께). */}
          <Route path="contracts" element={<ContractsPage />} />
          <Route path="contracts/:id" element={<ContractDetailPage />} />
          {/* MARKETING — 이벤트 참석자 모듈(CRM 하위) */}
          <Route path="marketing/dashboard" element={<MarketingDashboardPage />} />
          <Route path="marketing/events" element={<EventHubPage />} />
          <Route path="marketing/events/:eventId" element={<EventHubPage />} />
          {/* Opportunities = 파이프라인 통합 뷰(칸반·목록·캘린더). 정식 경로 = /crm/opportunities */}
          <Route path="opportunities" element={<CRMPipelinePage />} />
          <Route path="opportunities/:id" element={<ProjectFlowPage />} />
          <Route path="wbs" element={<WbsPortfolioPage />} />
          {/* 구 경로 /crm/projects → opportunities 로 리다이렉트(북마크 보존) */}
          {/* 수주 이후 관리 — opportunities 와 같은 UI, 수주 완료 건만.
              2026-06 에 /crm/projects 를 opportunities 로 통일했었는데, 그 뒤 "수주 이후"
              구간이 별도 화면으로 필요해져 다시 생겼다(구독 운영·청구 사이클). 되돌린 게
              아니라 보는 구간이 다른 화면이다 — opportunities 는 수주까지, 여기는 그 다음. */}
          <Route path="projects" element={<CRMProjectsPage />} />
          {/* 수주 이후 상세 — 같은 컴포넌트를 project 모드로. 전수주 조작(Lead 되돌리기)은 빠진다. */}
          <Route path="projects/:id" element={<ProjectFlowPage mode="project" />} />
          {/* 회의록 보기 — 활동의 "[회의록 링크]" 가 착지하는 곳. 본문 정본은 crm_meeting_notes. */}
          <Route path="meeting-notes/:id" element={<MeetingNoteViewPage />} />
          {/* 구 /crm/pipeline → Opportunities 로 통합·리다이렉트(북마크·기존 링크 보존) */}
          <Route path="pipeline" element={<Navigate to="/crm/opportunities" replace />} />
          {/* 구 경로 보존 — Task 화면은 /automation/poc/tasks 로 이전. */}
          <Route path="tasks" element={<Navigate to="/automation/poc/tasks" replace />} />
          <Route path="3d-simulation" element={<DtwinSandboxPage />} />
          {/* 옛 경로 호환 — dtwin-sandbox → 3d-simulation */}
          <Route path="dtwin-sandbox" element={<Navigate to="/crm/3d-simulation" replace />} />
          <Route path="dtwin-sample/stacker" element={<DtwinStackerSamplePage />} />
          <Route path="dtwin-sample/worker-motion" element={<DtwinWorkerMotionPage />} />
          <Route path="dtwin-sample/worker-motion-v2" element={<DtwinWorkerMotionV2Page />} />
          <Route path="pipeline-settings" element={<PipelineSettingsPage />} />
          {/* 수주 이후 실행 파이프라인 — 영업 파이프라인과 단계 구성이 달라 목록을 나눈다. */}
          <Route path="project-pipeline-settings" element={<PipelineSettingsPage scope="project" />} />
          {/* 옛 단독 플로우 빌더 — Opportunity 상세(V2)로 보낸다(?projectId= → 그 기회, 없으면 목록). */}
          <Route path="flow-builder" element={<FlowBuilderRedirect />} />
          {/* ★ 정본 경로. 아래 document-automation/* · /automation/* 은 전부 여기로 리다이렉트한다
              (쿼리·해시 보존 — DocumentRouteRedirect). 북마크·임베드·딥링크가 죽지 않는다. */}
          {/* 문서 영역 진입점. 목록 허브(목업 ②)는 Phase 4 — 그때 이 리다이렉트를 허브로 바꾼다. */}
          {/* /crm/documents 라는 주소는 없앴다(사용자 지정) — 사이드바도 견적서를 직접 가리킨다.
              옛 링크·북마크가 404 로 떨어지지 않게 리다이렉트만 남긴다. */}
          <Route path="documents" element={<Navigate to="/crm/documents/quote" replace />} />
          <Route path="documents/:docKey" element={<DocumentWorkspacePage />} />
          <Route path={crmRouteSegment("quote")} element={<DocumentRouteRedirect docKey="quote" />} />
          <Route path={crmRouteSegment("quote-other")} element={<DocumentRouteRedirect docKey="quote-other" />} />
          <Route path={crmRouteSegment("contract")} element={<DocumentRouteRedirect docKey="contract" />} />
          <Route path={crmRouteSegment("contract-integration")} element={<DocumentRouteRedirect docKey="contract-integration" />} />
          <Route path={crmRouteSegment("invoice")} element={<DocumentRouteRedirect docKey="invoice" />} />
          <Route path={crmRouteSegment("mou")} element={<DocumentRouteRedirect docKey="mou" />} />
          <Route path={crmRouteSegment("maintenance-mou")} element={<DocumentRouteRedirect docKey="maintenance-mou" />} />
          <Route path={crmRouteSegment("mou-dematic")} element={<DocumentRouteRedirect docKey="mou-dematic" />} />
          <Route path={crmRouteSegment("nda")} element={<DocumentRouteRedirect docKey="nda" />} />
          <Route path={crmRouteSegment("nda-international")} element={<DocumentRouteRedirect docKey="nda-international" />} />
          <Route path={crmRouteSegment("order-approval")} element={<DocumentRouteRedirect docKey="order-approval" />} />
          <Route path="api-console" element={<ApiConsolePage />} />
          <Route path="email-templates" element={<EmailTemplateBuilderPage />} />
          <Route path="automation-roadmap" element={<AutomationRoadmapPage />} />
          <Route path="automation/email-test" element={<EmailSendTestPage />} />
          <Route path="lead-mail-rules" element={<LeadMailRulesPage />} />
          <Route path="slack-templates" element={<SlackTemplatePage />} />
          {/* Email/Slack 템플릿 통합 화면. 위 email-templates / slack-templates 라우트는
              메뉴에서만 내리고 그대로 살려 둔다 — 북마크와 기존 링크가 죽지 않게. */}
          <Route path="communication-templates" element={<CommunicationTemplatesPage />} />
          <Route path="communication-templates/:channel" element={<CommunicationTemplatesPage />} />
          <Route path="communication-templates/:channel/:id" element={<CommunicationTemplatesPage />} />
          <Route path="lead-test" element={<LeadTestPage />} />
          {/* 통합 A안 단계 3 — 역량 4탭이 파트너 상세와 /partners/analysis 로 갈라졌다.
              구 화면은 당분간 그대로 두되(대량 입력 경로), 메뉴에서는 내렸다. */}
          <Route path="partner-capabilities" element={<PartnerCapabilitiesPage />} />
          {/* 모델별 가격은 Partner Capabilities 탭으로 편입됨 — 구 경로는 리다이렉트(북마크 보존) */}
          <Route path="partner-model-pricing" element={<Navigate to="/crm/partner-capabilities" replace />} />
          {/* 파트너 상세 › 역량 · 가격 › 패키지 매핑으로 이관 — 옛 북마크는 「패키지 기준」으로 보낸다. */}
          <Route path="package-partner-mapping" element={<Navigate to="/crm/partners?view=package-mapping" replace />} />
          <Route path="partner-verification" element={<PartnerVerificationPage />} />
          {/* ★ analysis 를 :id 보다 먼저 둬야 한다 — 뒤에 두면 id="analysis" 로 잡힌다 */}
          <Route path="partners/analysis" element={<PartnerAnalysisPage />} />
          <Route path="partners/:id" element={<PartnerDetailPage />} />
          <Route path="logistics-data-lab" element={<LogisticsDataLabPage />} />
          {/* 회사 자료 라이브러리 (COL-63) */}
          <Route path="company-assets" element={<CompanyAssetsPage />} />
          <Route path="files" element={<CRMFilesPage />} />
          <Route path={crmRouteSegment("proposal")} element={<ProposalPage />} />
          <Route path="document-automation/proposal-builder" element={<Navigate to="/automation/poc/proposal-builder" replace />} />
          <Route path="document-automation/proposal-templates" element={<Navigate to="/automation/poc/proposal-templates" replace />} />
          <Route path="document-automation/proposal-import" element={<Navigate to="/automation/poc/proposal-import" replace />} />
          <Route path="document-automation/proposal-compose" element={<Navigate to="/automation/poc/proposal-compose" replace />} />
          <Route path="document-automation/proposal-assets" element={<Navigate to="/automation/poc/proposal-assets" replace />} />
          <Route path="proposal-test" element={<ProposalPage />} />
          <Route path="proposal-form-sample" element={<CRMHtmlSamplePage />} />
          {/* 구 경로 → /automation POC 메뉴로 이동 (북마크 보존) */}
          <Route path="warehouse-rack-poc" element={<Navigate to="/automation/poc/warehouse-rack" replace />} />
          {/* Catch-all: redirect legacy /crm/pipeline to new route */}
          <Route path="*" element={<Navigate to="/crm/dashboard" replace />} />
        </Route>
        </Route>

        {/* 구 /admin → /settings 통합 (북마크 보존) */}
        <Route path="/admin">
          <Route index element={<Navigate to="/settings" replace />} />
          <Route path="users" element={<Navigate to="/settings/users" replace />} />
          <Route path="menus" element={<Navigate to="/settings/menus" replace />} />
          <Route path="announcements" element={<Navigate to="/settings/announcements" replace />} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
    </IntegrationsProvider>
    </DocumentCatalogProvider>
    </MenuRoleProvider>
    </AuthProvider>
    </ConfirmProvider>
    </ToastProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}
